import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import clsx from 'clsx';
import useReports from '../../hooks/useBreakpoint/useReports';
import { useToast } from '../../hooks/useToast';
import { FuzzyFilterBar } from '../../pages/AdminPublisherList/FuzzyFilterBar';
import { stores } from '../../stores';
import { useUsers } from '../../stores/userQuery';
import { Button } from '../Button/Button';
import { PageTitle } from '../PageTitle/PageTitle';
import { getReportTypeIcon } from '../ReportsOverview/utils';
import { Select } from '../Select/Select';
import { TopBar } from '../TopBar/TopBar';
import { ReportTemplateCard } from './ReportTemplateCard';
import { ReportTemplate, useReportTemplates } from './queries';

export function ReportTemplates() {
	const [, showToast] = useToast();
	const [filterIds, setFilterIds] = useState<string[] | undefined>(undefined);
	const [setAutoAnimate] = useAutoAnimate();
	const { data: users } = useUsers();
	const { availableReportTypes } = useReports();
	const [reportType, setReportType] = useState('all');
	const [showHidden, setShowHidden] = useState(false);

	const { data: templates = [], isLoading } = useReportTemplates();

	const reportTypes = useMemo(() => availableReportTypes.filter((t) => !t.noSaveType), [availableReportTypes]);
	const reportTypeOptions = useMemo(() => [
		{ value: 'all', label: 'All types', icon: null },
		...reportTypes.map((t) => ({
			value: t.type,
			label: t.DESCRIPTION,
			icon: <img src={getReportTypeIcon(t.type)} alt={t.DESCRIPTION} />,
		})),
	], [reportTypes]);

	const filteredTemplates = useMemo(() => {
		if (!templates) return [];
		return templates
			.filter((t) => (reportType === 'all' || t.settings.type === reportType) && !(t.hidden && !showHidden))
			.filter((t) => filterIds?.includes(t.id) ?? true)
			.sort((a, b) => a.settings.title.localeCompare(b.settings.title));
	}, [templates, reportType, showHidden, filterIds]);

	const filterData = useMemo(() => {
		if (!templates) return [];
		return templates.map((t) => ({
			id: t.id,
			text: t.settings.title,
			author: users?.find((u) => u.id === t.userId)?.fullname,
			data: t,
		}));
	}, [templates, users]);

	const handleFilter = useCallback(({ results }) => {
		setFilterIds(results?.map((r) => r.item.id));
	}, []);

	return (
		<>
			<TopBar
				title="Report templates"
			/>

			<div className="h-full w-full mx-auto">
				<PageTitle backlink={{ href: '/reports', hrefState: { new: true } }}>Use a report template</PageTitle>

				<div className="flex flex-wrap justify-between gap-4 mb-4">
					<FuzzyFilterBar
						placeholder="Find by title"
						value={new URLSearchParams(location.search).get('f') ?? ''}
						data={filterData}
						keys={['text', 'author']}
						onFilter={handleFilter}
						shortcut="$mod+F"
						className="w-[300px]"
					/>

					<div className="flex flex-wrap justify-end gap-2 ml-auto">
						<Select
							value={reportType}
							onChange={setReportType}
							options={reportTypeOptions}
							highlighted={reportType !== 'all'}
						/>

						{stores.identity.isAdministrator() && (
							<Button
								variant="tonal"
								href="/settings/report-templates"
							>
								Manage templates
							</Button>
						)}
					</div>
				</div>

				{/* Templates Grid */}
				<div className="min-h-[400px]">
					<TemplatesGrid
						ref={setAutoAnimate}
						templates={filteredTemplates}
						isLoading={isLoading}
					/>
					{!isLoading && filteredTemplates.length === 0 && (
						<div className="flex flex-col gap-2 items-center animate-fade-in">
							<span className="text-text-secondary">No templates found</span>
						</div>
					)}
				</div>
			</div>
		</>
	);
}

type TemplatesGridProps = {
	templates: ReportTemplate[];
	isLoading?: boolean;
};

const TemplatesGrid = forwardRef<HTMLDivElement, TemplatesGridProps>(({ templates, isLoading }, ref) => {
	return (
		<div
			ref={ref}
			className={clsx(
				'grid gap-6',
				'grid-cols-[repeat(auto-fill,minmax(360px,1fr))]',
			)}
		>
			{isLoading ? (
				// Show skeleton cards while loading
				Array.from({ length: 6 }).map((_, index) => (
					<ReportTemplateCard
						key={`skeleton-${index}`}
						skeleton={true}
					/>
				))
			) : (
				templates.map((template) => (
					<ReportTemplateCard
						key={template.id}
						template={template}
					/>
				))
			)}
		</div>
	);
});
